import axios from "axios";
import { toast } from "react-toastify";

export const setupInterceptors = (authDataRef, setAuthData, backUrl, navigate) => {

  /* ── REQUEST: auto-attach Bearer token ── */
  const reqId = axios.interceptors.request.use(
    (config) => {
      const token = authDataRef.current?.access;
      if (token && !config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  /* ── RESPONSE: silent token refresh on 401 ── */
  let isRefreshing = false;
  let sessionExpired = false; // blocks re-entry after logout
  let failedQueue = [];

  const processQueue = (token = null) => {
    failedQueue.forEach((p) => (token ? p.resolve(token) : p.reject()));
    failedQueue = [];
  };

  const handleSessionExpired = () => {
    if (sessionExpired) return; // already handled
    sessionExpired = true;

    setAuthData(null);
    localStorage.removeItem("authData");
    delete axios.defaults.headers.common["Authorization"];

    navigate("/");

    // Wait for login page to mount its ToastContainer, then show toast
    setTimeout(() => {
      const TOAST_ID = "session-expired";
      let seconds = 5;

      toast.warning(`Your session has expired. Please login again. (${seconds}s)`, {
        toastId: TOAST_ID,
        autoClose: 5000,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
      });

      const interval = setInterval(() => {
        seconds -= 1;
        if (seconds > 0) {
          toast.update(TOAST_ID, {
            render: `Your session has expired. Please login again. (${seconds}s)`,
            autoClose: seconds * 1000,
          });
        } else {
          clearInterval(interval);
        }
      }, 1000);
    }, 150);
  };

  const resId = axios.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config;

      // If session already expired, silently swallow all further errors
      if (sessionExpired) {
        return new Promise(() => {}); // never resolves — no error thrown
      }

      // Skip refresh for login endpoint to avoid loops
      if (originalRequest?.url?.includes("/api/token/") &&
          !originalRequest?.url?.includes("/api/token/refresh/")) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {

        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axios(originalRequest);
          }).catch(() => new Promise(() => {})); // swallow silently if refresh failed
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = authDataRef.current?.refresh;
          if (!refreshToken) throw new Error("No refresh token");

          const { data } = await axios.post(
            `${backUrl}/api/token/refresh/`,
            { refresh: refreshToken },
            { _retry: true }
          );

          const newAccessToken = data.access;

          // Update state + localStorage + ref + default header
          setAuthData((prev) => ({ ...prev, access: newAccessToken }));
          const stored = localStorage.getItem("authData");
          if (stored) {
            const parsed = JSON.parse(stored);
            parsed.access = newAccessToken;
            localStorage.setItem("authData", JSON.stringify(parsed));
          }
          authDataRef.current = { ...authDataRef.current, access: newAccessToken };
          axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          processQueue(newAccessToken);
          return axios(originalRequest);

        } catch {
          // Refresh failed — drain queue silently, then logout
          processQueue(null);
          handleSessionExpired();
          return new Promise(() => {}); // swallow the original request error silently

        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return () => {
    axios.interceptors.request.eject(reqId);
    axios.interceptors.response.eject(resId);
  };
};

export default axios;
